import React, { useMemo, useState } from "react";
import { formatINR, formatINRFull } from "../lib/api";

// Visually appealing wealth/ROI/LRD/Mortgage calculator
export default function WealthCalculator({ initialPrice = 50000000, initialRent = 350000 }) {
  const [price, setPrice] = useState(initialPrice);
  const [rent, setRent] = useState(initialRent);
  const [downPct, setDownPct] = useState(30);
  const [tenureYrs, setTenureYrs] = useState(15);
  const [interest, setInterest] = useState(9.25);
  const [escalation, setEscalation] = useState(15);
  const [escalateEvery, setEscalateEvery] = useState(3);
  const [holdYears, setHoldYears] = useState(9);

  const calc = useMemo(() => {
    const loan = price * (1 - downPct / 100);
    const r = interest / 12 / 100;
    const n = tenureYrs * 12;
    const emi = loan > 0 && r > 0 ? (loan * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1) : 0;

    let totalRent = 0;
    let yearlyRent = rent * 12;
    let monthlyRent = rent;
    const yearly = [];
    const monthlyJourney = [];
    let breakevenYear = null;
    for (let y = 1; y <= holdYears; y++) {
      if (y > 1 && (y - 1) % escalateEvery === 0) {
        yearlyRent = yearlyRent * (1 + escalation / 100);
        monthlyRent = monthlyRent * (1 + escalation / 100);
      }
      totalRent += yearlyRent;
      yearly.push({ y, rent: yearlyRent });
      const monthlyNet = monthlyRent - emi;
      monthlyJourney.push({ y, monthlyRent, monthlyNet, yearlyNet: monthlyNet * 12 });
      if (breakevenYear === null && monthlyNet >= 0) breakevenYear = y;
    }

    const annualRent1 = rent * 12;
    const grossYield = (annualRent1 / price) * 100;
    const monthlyNet = rent - emi;
    return { emi, loan, totalRent, grossYield, monthlyNet, yearly, monthlyJourney, breakevenYear };
  }, [price, rent, downPct, tenureYrs, interest, escalation, escalateEvery, holdYears]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6" data-testid="wealth-calculator">
      <div className="lg:col-span-2 rounded-2xl border border-neutral-200 bg-white p-6">
        <div className="label-tiny mb-4">Inputs</div>
        <div className="space-y-5">
          <Field label={`Property Price · ${formatINR(price)}`} testId="calc-price">
            <input type="range" min={5000000} max={300000000} step={500000} value={price} onChange={(e) => setPrice(+e.target.value)} className="w-full accent-[#5D33F6]" />
          </Field>
          <Field label={`Monthly Rent · ${formatINR(rent)}`} testId="calc-rent">
            <input type="range" min={50000} max={2000000} step={5000} value={rent} onChange={(e) => setRent(+e.target.value)} className="w-full accent-[#5D33F6]" />
          </Field>
          <Field label={`Down Payment · ${downPct}%`} testId="calc-down">
            <input type="range" min={10} max={70} step={5} value={downPct} onChange={(e) => setDownPct(+e.target.value)} className="w-full accent-[#5D33F6]" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={`Loan Tenure · ${tenureYrs} yrs`} testId="calc-tenure">
              <input type="range" min={5} max={20} step={1} value={tenureYrs} onChange={(e) => setTenureYrs(+e.target.value)} className="w-full accent-[#5D33F6]" />
            </Field>
            <Field label={`Interest · ${interest}%`} testId="calc-interest">
              <input type="range" min={7} max={13} step={0.05} value={interest} onChange={(e) => setInterest(+e.target.value)} className="w-full accent-[#5D33F6]" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label={`Escalation · ${escalation}%`} testId="calc-escalation">
              <input type="range" min={5} max={25} step={1} value={escalation} onChange={(e) => setEscalation(+e.target.value)} className="w-full accent-[#5D33F6]" />
            </Field>
            <Field label={`Every · ${escalateEvery} yrs`} testId="calc-escalate-every">
              <input type="range" min={1} max={5} step={1} value={escalateEvery} onChange={(e) => setEscalateEvery(+e.target.value)} className="w-full accent-[#5D33F6]" />
            </Field>
          </div>
          <Field label={`Hold Horizon · ${holdYears} yrs`} testId="calc-hold">
            <input type="range" min={1} max={15} step={1} value={holdYears} onChange={(e) => setHoldYears(+e.target.value)} className="w-full accent-[#5D33F6]" />
          </Field>
        </div>
      </div>

      <div className="lg:col-span-3 grid grid-cols-2 gap-4">
        <Stat label="Gross Yield" value={`${calc.grossYield.toFixed(2)}%`} accent testId="calc-gross-yield" />
        <Stat label="Monthly EMI" value={formatINR(calc.emi)} testId="calc-emi" />
        <Stat label="Year 1 · Monthly Net" value={formatINR(calc.monthlyNet)} accent={calc.monthlyNet >= 0} negative={calc.monthlyNet < 0} testId="calc-net" />
        <Stat label={`Total Rent · ${holdYears} yrs`} value={formatINR(calc.totalRent)} testId="calc-total-rent" />

        {/* Monthly Cashflow Journey */}
        <div className="col-span-2 rounded-2xl border border-neutral-200 bg-white p-6" data-testid="calc-monthly-journey">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-tiny text-brand">Monthly Cashflow Journey</div>
              <div className="font-heading text-lg font-medium tracking-tight mt-1">
                {calc.breakevenYear
                  ? (calc.breakevenYear === 1
                      ? <>Cashflow positive <span className="text-emerald-600">from Day 1</span>.</>
                      : <>Negative early. Turns <span className="text-emerald-600">positive in Year {calc.breakevenYear}</span>.</>)
                  : <>Stays sub-EMI for the full {holdYears}-yr horizon — try a higher down-payment.</>}
              </div>
            </div>
            <div className="hidden sm:flex items-center gap-3 text-[10px] uppercase tracking-wider font-bold text-neutral-400">
              <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-rose-500" /> Cash-out</span>
              <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" /> Cash-in</span>
            </div>
          </div>

          {(() => {
            const maxAbs = Math.max(...calc.monthlyJourney.map((m) => Math.abs(m.monthlyNet)), 1);
            return (
              <div className="flex items-stretch gap-1.5 h-40 border-y border-dashed border-neutral-200 relative">
                <div className="absolute left-0 right-0 top-1/2 h-px bg-neutral-300" />
                {calc.monthlyJourney.map((m) => {
                  const isPos = m.monthlyNet >= 0;
                  const h = (Math.abs(m.monthlyNet) / maxAbs) * 50;
                  return (
                    <div key={m.y} className="flex-1 flex flex-col items-center justify-center relative group">
                      <div className="absolute left-0 right-0 top-1/2 flex flex-col items-center" style={{ transform: "translateY(-100%)" }}>
                        {isPos && <div className="w-full mx-auto rounded-t-md bg-emerald-500 transition-all" style={{ height: `${h}%`, minHeight: "2px" }} />}
                      </div>
                      <div className="absolute left-0 right-0 top-1/2 flex flex-col items-center">
                        {!isPos && <div className="w-full mx-auto rounded-b-md bg-rose-500 transition-all" style={{ height: `${h}%`, minHeight: "2px" }} />}
                      </div>
                      <div className="absolute -top-7 opacity-0 group-hover:opacity-100 transition pointer-events-none bg-neutral-900 text-white text-[10px] font-medium px-1.5 py-0.5 rounded whitespace-nowrap z-10">
                        Y{m.y}: {isPos ? "+" : "−"}{formatINR(Math.abs(m.monthlyNet))}/mo
                      </div>
                      <div className="absolute -bottom-5 text-[10px] text-neutral-400 font-medium">Y{m.y}</div>
                    </div>
                  );
                })}
              </div>
            );
          })()}

          <div className="mt-9 grid grid-cols-3 gap-3 text-xs">
            <div>
              <div className="label-tiny">Year 1 · monthly</div>
              <div className={`font-medium mt-0.5 ${calc.monthlyJourney[0]?.monthlyNet >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                {calc.monthlyJourney[0]?.monthlyNet >= 0 ? "+" : "−"}{formatINR(Math.abs(calc.monthlyJourney[0]?.monthlyNet || 0))}
              </div>
            </div>
            <div>
              <div className="label-tiny">Year {Math.min(holdYears, Math.ceil(holdYears / 2))} · monthly</div>
              <div className={`font-medium mt-0.5 ${calc.monthlyJourney[Math.ceil(holdYears / 2) - 1]?.monthlyNet >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                {calc.monthlyJourney[Math.ceil(holdYears / 2) - 1]?.monthlyNet >= 0 ? "+" : "−"}{formatINR(Math.abs(calc.monthlyJourney[Math.ceil(holdYears / 2) - 1]?.monthlyNet || 0))}
              </div>
            </div>
            <div>
              <div className="label-tiny">Year {holdYears} · monthly</div>
              <div className={`font-medium mt-0.5 ${calc.monthlyJourney[calc.monthlyJourney.length - 1]?.monthlyNet >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                {calc.monthlyJourney[calc.monthlyJourney.length - 1]?.monthlyNet >= 0 ? "+" : "−"}{formatINR(Math.abs(calc.monthlyJourney[calc.monthlyJourney.length - 1]?.monthlyNet || 0))}
              </div>
            </div>
          </div>
        </div>

        <div className="col-span-2 rounded-2xl border border-neutral-200 bg-white p-6">
          <div className="label-tiny mb-3">Year-on-year rental projection</div>
          <div className="flex items-end gap-1.5 h-32">
            {calc.yearly.map((y) => {
              const max = Math.max(...calc.yearly.map((yy) => yy.rent));
              const h = (y.rent / max) * 100;
              return (
                <div key={y.y} className="flex-1 flex flex-col items-center gap-1">
                  <div className="w-full bg-brand rounded-t-md transition-all" style={{ height: `${h}%` }} />
                  <div className="text-[10px] text-neutral-400">Y{y.y}</div>
                </div>
              );
            })}
          </div>
          <div className="mt-3 grid grid-cols-3 gap-3 text-xs text-neutral-500">
            <div>Year 1 rent: <span className="text-neutral-900 font-medium">{formatINRFull(rent * 12)}/yr</span></div>
            <div>Year {holdYears} rent: <span className="text-neutral-900 font-medium">{formatINRFull(Math.round(calc.yearly[calc.yearly.length - 1]?.rent || 0))}/yr</span></div>
            <div>Loan amount: <span className="text-neutral-900 font-medium">{formatINR(calc.loan)}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, testId }) {
  return (
    <div data-testid={testId}>
      <div className="text-sm font-medium text-neutral-700 mb-2">{label}</div>
      {children}
    </div>
  );
}

function Stat({ label, value, accent, negative, testId }) {
  return (
    <div className={`rounded-2xl border ${accent ? "border-brand/40 bg-brand-50" : "border-neutral-200 bg-white"} p-5`} data-testid={testId}>
      <div className="label-tiny">{label}</div>
      <div className={`mt-1 font-heading text-2xl md:text-3xl font-medium tracking-tight ${negative ? "text-rose-600" : accent ? "text-brand-700" : "text-neutral-900"}`}>{value}</div>
    </div>
  );
}
